import React, { useEffect, useMemo, useState } from "react";
import CustomToolbar from "../global/CustomToolbar";
import SettingsIcon from "@material-ui/icons/Settings";
import Spinner from "./Spinner";
import { MONTHS, money0 } from "./utils";

const MODES = [
  { id: "weekly", label: "Weekly Report" },
  { id: "atm", label: "ATM Records" },
  { id: "annual", label: "Annual Profit Report" },
];

export default function Landing({ nav, api, currentWeek, locationNames, locations, isAdmin }) {
  const [tab, setTab] = useState("weekly");

  // Settings only surfaces for admins — non-admins never see the gear.
  const headerButtons = [];
  if (isAdmin) {
    headerButtons.push({
      icon: <SettingsIcon />,
      title: "Settings",
      handler: () => nav("settings"),
    });
  }

  return (
    <>
      <CustomToolbar title="Profit Tracker" headerButtons={headerButtons} />
      <div className="pt-content">
        <div className="pt-tabs" role="tablist">
          {MODES.map((m) => (
            <button
              key={m.id}
              role="tab"
              aria-selected={tab === m.id}
              className={`pt-tab ${tab === m.id ? "is-active" : ""}`}
              onClick={() => setTab(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {tab === "weekly" && (
          <YearNav nav={nav} mode="weekly" currentWeek={currentWeek} />
        )}
        {tab === "atm" && (
          <YearNav nav={nav} mode="atm" currentWeek={currentWeek} />
        )}
        {tab === "annual" && (
          <AnnualReport api={api} locationNames={locationNames} locations={locations} />
        )}
      </div>
    </>
  );
}

function YearNav({ nav, mode, currentWeek }) {
  const amber = mode === "atm";
  const thisYear = new Date().getFullYear();
  const years = [thisYear, thisYear - 1];

  const handleCurrent = () => {
    if (!currentWeek?.week) return;
    nav("detail", {
      year: currentWeek.year,
      month: MONTHS[currentWeek.month - 1],
      week: currentWeek.week,
      mode,
    });
  };

  return (
    <div>
      {currentWeek?.week && (
        <button
          className={`pt-listbtn cta ${amber ? "amber" : ""}`}
          onClick={handleCurrent}
        >
          <div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
              Latest Week
            </div>
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 800, marginTop: 3 }}>
              {currentWeek.week.label}, {currentWeek.year}
            </div>
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11, marginTop: 2 }}>
              {currentWeek.week.id} · {MONTHS[currentWeek.month - 1]} {currentWeek.year}
            </div>
          </div>
          <div style={{ color: "#fff", fontSize: 22 }}>→</div>
        </button>
      )}

      <div className="pt-lb" style={{ marginTop: 16 }}>Accounting Years</div>
      {years.map((y) => (
        <button
          key={y}
          className="pt-listbtn"
          onClick={() => nav("months", { year: y, mode })}
        >
          <div>
            <div className="year">{y}</div>
            <div className="meta">Accounting Year</div>
          </div>
          <div className={`arrow-box ${y === thisYear ? (amber ? "amber" : "teal") : ""}`}>→</div>
        </button>
      ))}
    </div>
  );
}

// ───────── Annual report ────────────────────────────────────────
function AnnualReport({ api, locationNames, locations }) {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const archivedNames = useMemo(
    () => new Set((locations || []).filter((l) => l.archived).map((l) => l.name)),
    [locations],
  );

  useEffect(() => {
    if (!locationNames.length) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    api.fetchAnnual(
      { year, locationNames },
      (res) => {
        setData(res?.profitSummary || res || []);
        setLoading(false);
      },
      (err) => {
        setError(typeof err === "string" ? err : "Failed to load annual report");
        setLoading(false);
      },
    );
  }, [year, locationNames, api]);

  // normalise into { [monthName]: { weeks: N, data: { [locName]: [w1,...] } } }
  // An archived location is dropped ONLY when it contributed nothing all year;
  // an archived location that earned in this year keeps its column so history
  // stays intact. Active locations are always kept.
  const byMonth = useMemo(() => {
    const out = {};
    if (!Array.isArray(data)) return out;
    for (const loc of data) {
      const name = loc.locationName || loc.name;
      const months = loc.months || {};
      if (archivedNames.has(name)) {
        const yearTotal = Object.values(months).reduce(
          (s, md) => s + (md.weeks || []).reduce((a, w) => a + (Number(w.profit) || 0), 0),
          0,
        );
        if (yearTotal === 0) continue; // archived + no value this year → hide
      }
      for (const [mn, mdata] of Object.entries(months)) {
        if (!out[mn]) out[mn] = { weeks: 0, data: {} };
        const weeks = mdata.weeks || [];
        const arr = weeks.map((w) => Number(w.profit) || 0);
        out[mn].weeks = Math.max(out[mn].weeks, arr.length);
        out[mn].data[name] = arr;
      }
    }
    return out;
  }, [data, archivedNames]);

  const activeMonths = MONTHS.filter((m) => byMonth[m] && byMonth[m].weeks > 0);

  const monthTotal = (mn) => {
    const md = byMonth[mn];
    if (!md) return 0;
    return Object.values(md.data).reduce(
      (s, arr) => s + arr.reduce((a, b) => a + (Number(b) || 0), 0),
      0,
    );
  };
  const ytd = activeMonths.reduce((s, m) => s + monthTotal(m), 0);
  const avg = activeMonths.length ? ytd / activeMonths.length : 0;
  const maxMonth = activeMonths.reduce((m, mn) => Math.max(m, monthTotal(mn)), 1);

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {[thisYear, thisYear - 1].map((y) => (
          <button
            key={y}
            className={`pt-yearpill ${year === y ? "is-active" : ""}`}
            onClick={() => setYear(y)}
          >
            {y}
          </button>
        ))}
      </div>

      {loading && <Spinner label="Loading annual report…" />}
      {error && <div className="pt-error">{error}</div>}

      {!loading && !error && activeMonths.length === 0 && (
        <div className="pt-empty">No annual data for {year}</div>
      )}

      {!loading && activeMonths.length > 0 && (
        <div className="pt-hero">
          <div className="lb">Year-to-Date · {year}</div>
          <div className="big">{money0(ytd)}</div>
          <div className="stats">
            <div className="stat">
              <div>
                <div className="k">Months</div>
                <div className="v">
                  {activeMonths.length}
                  <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>/12</span>
                </div>
              </div>
            </div>
            <div className="sep" />
            <div className="stat">
              <div>
                <div className="k">Avg / Month</div>
                <div className="v">{money0(avg)}</div>
              </div>
            </div>
          </div>
          <div className="pt-bars">
            {activeMonths.map((mn) => {
              const t = monthTotal(mn);
              return (
                <div key={mn} className="c">
                  <div className="track">
                    <div
                      className="fill"
                      style={{ height: `${Math.max(2, (t / maxMonth) * 100)}%` }}
                    />
                  </div>
                  <div className="lb">{mn.substring(0, 3)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeMonths.map((mn, mi) => (
        <MonthTable
          key={mn}
          monthName={mn}
          monthData={byMonth[mn]}
          prevMonthData={mi > 0 ? byMonth[activeMonths[mi - 1]] : null}
        />
      ))}
    </div>
  );
}

function MonthTable({ monthName, monthData, prevMonthData }) {
  const locNames = Object.keys(monthData.data).sort();
  const weekCount = monthData.weeks;

  const locMonthTotal = (name, md) =>
    (md?.data?.[name] || []).reduce((s, v) => s + (Number(v) || 0), 0);
  const weekAll = (wi) =>
    locNames.reduce((s, l) => s + (monthData.data[l]?.[wi] ?? 0), 0);
  const grand = locNames.reduce((s, l) => s + locMonthTotal(l, monthData), 0);
  const prevGrand = prevMonthData
    ? locNames.reduce((s, l) => s + locMonthTotal(l, prevMonthData), 0)
    : 0;
  const grandPct = prevGrand ? ((grand - prevGrand) / Math.abs(prevGrand)) * 100 : 0;

  return (
    <div className="pt-card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px 8px" }}>
        <div style={{ fontSize: 18, fontWeight: 800 }}>{monthName}</div>
      </div>
      <div className="pt-tbl-scroll">
        <table className="pt-tbl" style={{ minWidth: 140 + locNames.length * 100 }}>
          <thead>
            <tr>
              <th style={{ position: "sticky", left: 0, background: "var(--pt-card-alt)", zIndex: 2 }}>
                Week
              </th>
              {locNames.map((l) => (
                <th key={l} style={{ whiteSpace: "nowrap" }}>{l}</th>
              ))}
              <th style={{ color: "var(--pt-amber-txt)", borderLeft: "2px solid var(--pt-amber)", whiteSpace: "nowrap" }}>
                ALL TOTAL
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: weekCount }, (_, wi) => {
              const wt = weekAll(wi);
              return (
                <tr key={wi}>
                  <td style={{ position: "sticky", left: 0, background: "var(--pt-card)", zIndex: 1, fontWeight: 700 }}>
                    W{wi + 1}
                  </td>
                  {locNames.map((l) => {
                    const v = monthData.data[l]?.[wi];
                    const color =
                      v == null
                        ? "var(--pt-muted-2)"
                        : v < 0
                        ? "var(--pt-red)"
                        : v === 0
                        ? "var(--pt-muted-2)"
                        : "var(--pt-text)";
                    return (
                      <td key={l} style={{ color }}>
                        {v == null ? "-" : v < 0 ? `-${money0(Math.abs(v))}` : money0(v)}
                      </td>
                    );
                  })}
                  <td style={{ color: "var(--pt-amber-txt)", fontWeight: 800, borderLeft: "2px solid rgba(217,119,6,0.2)", background: "rgba(251,191,36,0.04)" }}>
                    {money0(wt)}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td style={{ position: "sticky", left: 0, background: "#f0fdf4", zIndex: 1 }}>
                Total
              </td>
              {locNames.map((l) => {
                const t = locMonthTotal(l, monthData);
                const pt = prevMonthData ? locMonthTotal(l, prevMonthData) : 0;
                const pc = pt ? ((t - pt) / Math.abs(pt)) * 100 : 0;
                return (
                  <td key={l}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                      {prevMonthData !== null && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: pc >= 0 ? "var(--pt-green)" : "var(--pt-red)" }}>
                          {pc >= 0 ? "▲" : "▼"}
                          {Math.abs(pc).toFixed(pc === 0 ? 0 : 1)}%
                        </span>
                      )}
                      <span style={{ color: t < 0 ? "var(--pt-red)" : "var(--pt-text)", fontWeight: 800 }}>
                        {t < 0 ? "-" : ""}{money0(Math.abs(t))}
                      </span>
                    </div>
                  </td>
                );
              })}
              <td style={{ borderLeft: "2px solid rgba(217,119,6,0.3)", background: "rgba(251,191,36,0.08)" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                  {prevMonthData !== null && (
                    <span style={{ fontSize: 9, fontWeight: 700, color: grandPct >= 0 ? "var(--pt-green)" : "var(--pt-red)" }}>
                      {grandPct >= 0 ? "▲" : "▼"}
                      {Math.abs(grandPct).toFixed(grandPct === 0 ? 0 : 1)}%
                    </span>
                  )}
                  <span style={{ color: "var(--pt-amber-txt)", fontWeight: 900, fontSize: 13 }}>
                    {money0(grand)}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

